import React, { useState } from "react";
import Breadcrumb from "../../../Components/Common/Breadcrumb";
import { faAdd } from "@fortawesome/free-solid-svg-icons";
import Table from "../../../Components/Common/Table";
import TableHead from "../../../Components/Common/TableHead";
import useSWR from "swr";
import { BlogApiUrls } from "../../../Api/Auth/ApiUrls";
import useTable from "../../../hooks/useTable";
import QueryString from "qs";
import BlogRow from "./Components/BlogRow";
import { Loader } from "../../../Components/Common/Loader";
import NoData from "../../../Components/Common/NoData";
import Pagination from "../../../Components/Common/Pagination/Pagination";
import { useLocation } from "react-router-dom";
import Modal from "../../../Components/Common/Modal";
import BlogForm from "./Components/BlogForm";
import useSWRMutation from "swr/mutation";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
} from "../../../Api/swrConfig";
import { toast } from "react-toastify";
import useToastError from "../../../hooks/useErrorToast";
import Storage from "../Storage";

const generateSlug = (title) =>
  title
    .toLowerCase()
    .trim()
    .replace(/[\s]+/g, "-")
    .replace(/[^\w-]+/g, "");

const tableColumnData = [
  { id: "title", label: "Title" },
  { id: "description", label: "Description" },
  { id: "slug", label: "Slug" },
  { id: "cover", label: "Cover" },
  { id: "createdAt", label: "Created" },
];

const BlogsList = () => {
  const [row, setRow] = useState(null);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    title: "",
    slug: "",
    description: "",
    link: "",
  });
  const [isModalOpen, setModalOpen] = useState(false);
  const [isStorageModalOpen, setStorageModalOpen] = useState(false);
  const location = useLocation();
  const { showErrorToast } = useToastError();

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useTable({ defaultOrderBy: "" });

  const apiQueryParams = {
    offset: (page - 1) * rowsPerPage,
    limit: rowsPerPage,
  };
  const apiQueryString = QueryString.stringify(apiQueryParams);

  const { isLoading, data, mutate } = useSWR(
    BlogApiUrls.list(apiQueryString)
  );

  const handleInputChange = (name, value) => {
    setFormData((prevState) => {
      const updatedData = { ...prevState, [name]: value };

      if (name === "title") {
        updatedData.slug = generateSlug(value);
      }

      return updatedData;
    });
  };

  const onEditClick = (row) => {
    setRow(row);
    setFormData({
      title: row.title,
      slug: row.slug,
      description: row.description || "",
      link: row.link || "",
    });
    setModalOpen(true);
  };

  const { trigger, isMutating } = useSWRMutation(
    BlogApiUrls.create,
    sendPostRequestAuth,
    {
      onSuccess: () => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const { trigger: editTrigger, isMutating: editMutating } = useSWRMutation(
    BlogApiUrls.edit(row?._id),
    sendPutRequestAuth,
    {
      onSuccess: () => {
        toast.success("Success");
        mutate();
        setModalOpen(false);
      },
      onError: (error) => {
        showErrorToast(error);
      },
    }
  );

  const actionButtons = [
    {
      label: "Create",
      icon: faAdd,
      onClick: () => {
        setRow(null);
        setFormData({
          title: "",
          slug: "",
          description: "",
          link: "",
        });
        setModalOpen(true);
      },
    },
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title) newErrors.title = "Title is required";
    if (!formData.slug) newErrors.slug = "Slug is required";
    if (!formData.description) newErrors.description = "Description is required";
    if (!formData.link) newErrors.link = "Cover image is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const payload = {
      ...formData,
    };

    if (row) {
      editTrigger(payload);
    } else {
      trigger(payload);
    }
  };

  return (
    <div>
      <Breadcrumb actionButtons={actionButtons} />
      <Table>
        <TableHead rowCount={data?.data.length} headerData={tableColumnData} />
        <tbody className="divide-y divide-gray-200">
          {!isLoading ? (
            data?.data.map((row) => (
              <BlogRow
                key={row._id}
                row={row}
                mutate={mutate}
                onEditClick={onEditClick}
              />
            ))
          ) : (
            <tr>
              <td colSpan="100" className="text-center py-4">
                <Loader />
              </td>
            </tr>
          )}
        </tbody>
        <NoData isNotFound={!data?.data.length && !isLoading} />
      </Table>
      <Pagination
        totalRows={data?.pagination?.totalItems || 0}
        currentPage={page}
        rowsPerPage={rowsPerPage}
        currentPath={location.pathname}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Modal
        isOpen={isModalOpen}
        title={row ? "Edit Blog" : "Create Blog"}
        onConfirm={handleSubmit}
        onCancel={() => {
          setErrors({});
          setModalOpen(false);
        }}
        isLoading={isMutating || editMutating}
        
      >
        <BlogForm
          errors={errors}
          handleSubmit={handleSubmit}
          onChange={handleInputChange}
          formData={formData}
          setModalOpen={setStorageModalOpen}
        />
      </Modal>
      <Modal
        isOpen={isStorageModalOpen}
        title={"Storage Manager"}
        onCancel={() => {
          setStorageModalOpen(false);
        }}
        footer={false}
      >
        <Storage
          isSelector
          onConfirm={(file) => {
            if (Array.isArray(file) && file.length)
              handleInputChange("link", file[0]?.fileUrl);
            setStorageModalOpen(false);
          }}
        />
      </Modal>
    </div>
  );
};

export default BlogsList;
